import * as React from "react";
import * as taskService from "../../../models/services/taskService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { useState } from "react";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Alert, Box, Grid } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { TextFieldWithButtonGroup } from "source/components/framework/Form/TextFieldWithButtonGroup";
import { ReportExcelReport } from "source/components/framework/Report/ReportExcelReport";
import { IReportGenerated2, ReportGenerated2 } from "source/models/report/reportGenerated2";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { DownloadButton } from "source/components/framework/Button/DownloadButton/DownloadButton";
import { AppUploadIcon } from "source/components/framework/Icon/AppUploadIcon";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRow, SaveTaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ReportGeneratedTableData } from "source/models/report/reportGeneratedTableData";
import { TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { TaskValidationRow } from "ui/Scripts/source/models/task/taskValidationRow";
import { TaskValidationRowCol } from "ui/Scripts/source/models/task/taskValidationRowCol";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

interface ITaskDataRowImportDialogProps {
    companyId: string;
    taskId: string;
    taskType: ITaskEditTypeItem;
    onOk: () => void;
    onCancel: () => void;
}

interface ITaskDataRowImportDialogState {
    file: File;
    //List
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    taskDataRows: ITaskDataRow[];
    reportData: IReportGenerated2;
}

export const TaskDataRowImportDialog = (props: ITaskDataRowImportDialogProps) => {
    const [state, setState] = useState<ITaskDataRowImportDialogState>(
        {
            file: null,
            //List
            sortColumn: "userCode",
            sortOrderIsAsc: true,
            selectedId: "",
            checkedIds: [],
            taskDataRows: [],
            reportData: null
        }
    );

    const handleCancel = () => {
        props.onCancel();
    };

    const getSaveData = (checkedIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("taskId", props.taskId);
        result.append("companyId", props.companyId);
        const taskDataRows: SaveTaskDataRow[] = [];
        checkedIds.forEach(id => {
            const item = state.reportData.tables[0].dataRows.find(i => i.id === id);
            if (!item) return;
            taskDataRows.push(SaveTaskDataRow.createSaveTaskDataRow(props.taskType.formCols, item, item.cols));
        });
        if (!taskDataRows.length) return;
        result.append("taskDataRows", JSON.stringify(taskDataRows));
        return result;
    };
    
    const importUsers = async (companyId: string, checkedIds: string[]): Promise<ApiSuccess> => {
        if (checkedIds.length < 1) return;
        const saveData = getSaveData(checkedIds);
        //Call server
        return await AppUtils.callService(() => taskService.importTaskDataRows(saveData.obj));
    };

    const handleOk = async () => {
        const result = await importUsers(props.companyId, state.checkedIds);
        if (!result) return null;
        props.onOk();
    };

    const uploadFile = async (files: File[]) => {
        if (!files || files.length < 1) return;
        const file = files[0];
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        setState((old) => ({
            ...old,
            file
        }));
        const saveData = new SaveDataObj();
        saveData.append("companyId", props.companyId);
        saveData.append("taskId", props.taskId);
        saveData.append("attachments", await SaveAttachmentBase64.fromFileAttachments([attachment]));
        // Call server
        const taskDataRows = await AppUtils.callService(() => taskService.uploadTaskDataRowImportFile(saveData.obj));
        if (!taskDataRows) return;
        if (!taskDataRows.items || !taskDataRows.items.length) {
            AppUtils.showErrorMessage(String.format(Translations.FileParameterDoesNotContainAnyTaskDataRows, file.name));
            return;
        }
        // Initialize and evaluate expressions
        const initializedFromOtherDataRows = props.taskType.hasColumnsInitializedFromOtherDataRows();
        let firstTaskDataRowCols: ITaskDataRowCol[] = [];
        let previousTaskDataRow: ITaskDataRow = null;
        for (const taskDataRow of taskDataRows.items) {
            const dataCols = taskDataRow.cols.slice(0);
            // Override values from other data rows
            if (initializedFromOtherDataRows) {
                let defaultColValues: ITaskDataRowCol[] = [];
                if (!previousTaskDataRow) {
                    const defaultValues = await AppUtils.callService(() => taskService.getTaskDataRowDefaultValues(props.companyId, props.taskId), false);
                    if (defaultValues?.cols?.length) {
                        defaultColValues = defaultValues.cols;
                        firstTaskDataRowCols = defaultColValues;
                    }
                } else {
                    // console.log("previousTaskDataRow", previousTaskDataRow);
                    defaultColValues = TaskDataRow.getNewRowDefaultValuesFromTaskDataRow(props.taskType, previousTaskDataRow, firstTaskDataRowCols);
                }
                // console.log("defaultColValues", defaultColValues);
                if (defaultColValues.length) {
                    for (const defaultColValue of defaultColValues) {
                        const dataCol = dataCols.find(i => i.field === defaultColValue.field);
                        if (!dataCol) {
                            dataCols.push(defaultColValue);
                        } else {
                            dataCol.valueStr = defaultColValue.valueStr;
                            dataCol.valueNbr = defaultColValue.valueNbr;
                        }
                    }
                }
            }
            // console.log("taskDataRow 1", taskDataRow);
            taskDataRow.initializeToTaskType(props.taskType, dataCols);
            TaskEditTypeItem.evaluateColsExpressions(taskDataRow.cols, props.taskType.formCols);
            // console.log("taskDataRow 2", taskDataRow);
            previousTaskDataRow = taskDataRow;
        }
        const checkedIds: string[] = [];
        // Set report data
        const reportData = new ReportGenerated2();
        const reportDataTable = new ReportGeneratedTableData();
        reportDataTable.taskType = props.taskType;
        reportDataTable.dataRows = taskDataRows.items;
        reportDataTable.validationRows = [];
        reportData.tables = [reportDataTable];
        // Validate and add validation rows
        for (const taskDataRow of taskDataRows.items) {
            const invalidCols = TaskTypeCol.getAllInvalidRequiredTaskTypeCols(props.taskType.formCols, taskDataRow.cols);
            if (!invalidCols.length) {
                checkedIds.push(taskDataRow.id);
                continue;
            }
            const taskValidationRow = new TaskValidationRow();
            taskValidationRow.id = taskDataRow.id;
            taskValidationRow.cols = invalidCols.map(i => {
                const taskValidationRowCol = new TaskValidationRowCol();
                taskValidationRowCol.field = i.field;
                taskValidationRowCol.errors = [String.format(Translations.FieldParameterValueMustBeDefined, i.title)];
                return taskValidationRowCol;
            });
            reportDataTable.validationRows.push(taskValidationRow);
        }
        setState((old) => ({
            ...old,
            taskDataRows: taskDataRows.items,
            reportData,
            checkedIds
        }));
    };

    const downloadTemplate = async () => {
        const fileName = await AppUtils.callService(() => taskService.getTaskDataRowImportTemplate(props.companyId, props.taskId, props.taskType.id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.DocumentParameterDownloaded, fileName));
    };

    const handleSetSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        setState((old) => ({
            ...old,
            sortColumn,
            sortOrderIsAsc
        }));
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState((old) => ({
            ...old,
            selectedId,
            checkedIds
        }));
    };

    return (
        <Dialog
            id="taskDataRowImportDialog"
            maxWidth="xl"
            maxHeight="90%"
            title={Translations.ImportTaskDataRows}
            body={
                <VerticalContainer
                    before={<>
                        <GridRow>
                            <Grid item lg={6}>
                                <FormField title={Translations.ExcelFile} margin={0}>
                                    <TextFieldWithButtonGroup
                                        type="text"
                                        name="userCode"
                                        title={Translations.ExcelFile}
                                        disabled={true}
                                        value={state.file?.name ?? ""}
                                        showButtons={true}
                                        buttons={[
                                            { label: Translations.UploadDocument, startIcon: <AppUploadIcon />, onClick: null, fileInput: { onChange: uploadFile, accept: ".xlsx,.xlsm" } }
                                        ]}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item lg={6}>
                                <FormField title={<span>&nbsp;</span>} margin="0 0 0 -10px" padding="0 0 0 10px">
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <DownloadButton sx={{ marginLeft: "auto" }} onClick={downloadTemplate}>{Translations.DownloadFileTemplate}</DownloadButton>
                                    </Box>
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.TaskDataRowsToBeImported}>
                                    {!state.taskDataRows.length &&
                                        <Alert severity="info">{Translations.ImportTaskDataRowsDialogInstruction}</Alert>
                                    }
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>}
                    main={
                        <ReportExcelReport
                            margin="0"
                            height="100%"
                            reportData={state.reportData}
                            sortingDisabled={false}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            multiSelect={true}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSortData={handleSetSortData}
                            onSetSelectedIds={handleSetSelectedIds}
                        />
                    }
                />
            }
            buttons={[
                { title: Translations.Close, variant: "outlined", onClick: handleCancel },
                { title: Translations.Import, disabled: !state.checkedIds.length, variant: "confirmation", onClick: handleOk },
            ]}
            onClose={handleCancel}
        />
    );
};